// checkout/steps/accountDetailsListeners.ts

import { checkoutData } from '../init';
import { submitFormAndUpdateStep } from '../utils/submitForm';
import { toggleLoadingSpinner } from '../utils/toggleLoadingSpinner';
import { applyFocusTrap } from '../../utils/focusTrap';
import { registerStepFocusCallback } from '../utils/stepFocusCallbacks';

/**
 * Initialises the account details form by binding the submit handler,
 * wiring focus transitions and keyboard/UX helpers.
 */
export function addAccountDetailsListeners(): void {
	/** It's important that we don't initialise the payment buttons until the account details step is focused. This is
	 * because the Apple Pay button needs to load the order data at the point of initialisation, and this will fail if
	 * the delivery groups haven't been created yet.
	 */
	registerStepFocusCallback('accountDetails', () => {
		const paymentMethods = {
			PayPal: {
				token: document.querySelector("meta[name='_paypaltoken']")?.getAttribute('content'),
				init: (token: string, containerId: string) =>
					window.braintreeFunctions!.initialisePayPalButton(token, containerId),
			},
			GooglePay: {
				token: document
					.querySelector("meta[name='_googlepaytoken']")
					?.getAttribute('content'),
				init: (token: string, containerId: string) =>
					window.braintreeFunctions!.initialiseGooglePayButton(token, containerId),
			},
			ApplePay: {
				token: document
					.querySelector("meta[name='_applepaytoken']")
					?.getAttribute('content'),
				init: (token: string, containerId: string) =>
					window.braintreeFunctions!.initialiseApplePayButton(token, containerId),
			},
		};

		for (const [key, { token, init }] of Object.entries(paymentMethods)) {
			if (token !== undefined && token !== null) {
				const containerId = String(key).toLowerCase() + `-express-button-container`;
				const container = document.getElementById(containerId);
				if (container) {
					init(token, containerId).catch((err) => {
						console.error(err);
					});
				}
			}
		}
	});

	const form = document.getElementById('accountDetailsForm') as HTMLFormElement | null;
	if (!form) return;

	// Submit handler with DOB guard
	form.addEventListener('submit', async (e) => {
		e.preventDefault();

		// Remove empty hidden address fields before submission
		const hiddenAddressInputs = form.querySelectorAll(
			'input[type="hidden"][id*="deliveryAddress-"], input[type="hidden"][id*="billingAddress-"]',
		);

		hiddenAddressInputs.forEach((input) => {
			const inputElement = input as HTMLInputElement;
			if (!inputElement.value || inputElement.value.trim() === '') {
				inputElement.remove();
			}
		});

		const action = checkoutData.accountDetails?.action;
		await submitFormAndUpdateStep(form, action);

		const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement | null;
		if (!submitBtn) return;
		const paymentMethodInput = form.querySelector('#paymentMethod') as HTMLInputElement | null;
		if (paymentMethodInput) return;
		toggleLoadingSpinner(submitBtn, 'Submitting');
	});

	// Allow Enter in password field to submit the form
	// form.addEventListener('keydown', (e: KeyboardEvent) => {
	// 	if (e.key === 'Enter') {
	// 		const target = e.target as HTMLElement;
	// 		if (target instanceof HTMLInputElement && target.type === 'password') {
	// 			e.preventDefault();
	// 			form.requestSubmit();
	// 		}
	// 	}
	// });

	/**
	 * Prevent Enter from submitting unless on the submit button.
	 */
	form.addEventListener('keydown', (e: KeyboardEvent) => {
		if (e.key === 'Enter') {
			const active = document.activeElement as HTMLElement | null;
			const isSubmit =
				active?.tagName === 'BUTTON' && (active as HTMLButtonElement).type === 'submit';
			if (!isSubmit) e.preventDefault();
		}
	});

	/**
	 * Toggle standard checkout form fields
	 */
	const standardCheckoutBtn = form.querySelector('#checkout-standard-btn') as HTMLElement;
	if (!standardCheckoutBtn) return;
	standardCheckoutBtn.addEventListener(
		'click',
		(e) => {
			e.preventDefault();
			const standardCheckoutFormFields = form.querySelector(
				'#checkout-standard-form-fields',
			) as HTMLElement;
			if (standardCheckoutFormFields) {
				standardCheckoutBtn.hidden = !standardCheckoutBtn.hidden;
				standardCheckoutFormFields.hidden = !standardCheckoutFormFields.hidden;
				applyFocusTrap(standardCheckoutFormFields, true);
			}
		},
		{ once: true },
	);
}
